import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { DialogueAlert } from '@thoughtspot/radiant-react/widgets/alert/dialogue/dialogue-alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { PopoverToggle } from '@thoughtspot/radiant-react/widgets/popover';
import React, { useEffect, useRef, useState } from 'react';
import styles from './impersonation-indicator.module.scss';

const IMPERSONATION_ICON_SRC =
    '/node_modules/@thoughtspot/radiant-styles/public/img/impersonation.svg';

// Show the ending-soon toast when the session has less than this many
// seconds left; it stays visible and counts down until the session ends.
const WARNING_THRESHOLD_SECONDS = 60;

export interface ImpersonationIndicatorProps {
    /** Display name of the user currently being impersonated. */
    userDisplayName: string;
    /**
     * Called when the admin confirms "Exit" in the exit-session dialog to
     * stop impersonating.
     * TODO(SCAL-325654): wire this to the exit-impersonation backend API
     * once it is available. For now the caller decides what happens.
     */
    onExitSession: () => void;
    /**
     * Epoch millis when the impersonation session expires, as reported by the
     * session-info response. The countdown is derived from this on every tick
     * rather than decremented, so a throttled or delayed interval (background
     * tab, device sleep, long task) cannot make the displayed time drift away
     * from the real expiry.
     */
    sessionEndTime: number;
}

export enum ImpersonationIndicatorTestIDs {
    IconButton = 'impersonation-icon-button',
    ExitButton = 'impersonation-exit-session',
    Timer = 'impersonation-timer',
}

/**
 * Seconds left until `sessionEndTime`, never negative. Rounded up so the
 * countdown shows 00:00:00 only once the session has actually expired.
 */
export const getRemainingSeconds = (sessionEndTime: number): number =>
    Math.max(0, Math.ceil((sessionEndTime - Date.now()) / 1000));

/** Formats a non-negative number of seconds as HH:MM:SS. */
export const formatCountdown = (totalSeconds: number): string => {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Indicator rendered in the top nav while impersonation is active. Shows a
 * circular icon button; clicking it opens a popover with the impersonated
 * user, a countdown, and an "Exit session" action. Exiting is confirmed via
 * a dialog, and a warning toast is shown when the session is about to end.
 */
export const ImpersonationIndicator: React.FC<ImpersonationIndicatorProps> = ({
    userDisplayName,
    onExitSession,
    sessionEndTime,
}) => {
    const { t } = useTranslation();
    const [remainingSeconds, setRemainingSeconds] = useState(() =>
        getRemainingSeconds(sessionEndTime),
    );
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    // Lets the admin dismiss the ending-soon toast before the session ends.
    const [isWarningToastDismissed, setIsWarningToastDismissed] = useState(
        false,
    );
    // Ensures the automatic exit at 00:00:00 fires only once per session.
    const hasAutoExitedRef = useRef(false);
    // Keep the latest onExitSession without re-triggering the timer effects.
    const onExitSessionRef = useRef(onExitSession);
    onExitSessionRef.current = onExitSession;

    useEffect(() => {
        setRemainingSeconds(getRemainingSeconds(sessionEndTime));
        setIsWarningToastDismissed(false);
        hasAutoExitedRef.current = false;
        const intervalId = window.setInterval(() => {
            setRemainingSeconds(getRemainingSeconds(sessionEndTime));
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [sessionEndTime]);

    useEffect(() => {
        // Session expired: automatically exit ("AUTO EXIT" in the design).
        if (remainingSeconds <= 0 && !hasAutoExitedRef.current) {
            hasAutoExitedRef.current = true;
            onExitSessionRef.current();
        }
    }, [remainingSeconds]);

    // The ending-soon toast is shown for the final minute and counts down
    // live until the session ends (or the admin dismisses/ends it).
    const isWarningToastVisible =
        remainingSeconds > 0 &&
        remainingSeconds < WARNING_THRESHOLD_SECONDS &&
        !isWarningToastDismissed;

    const warningToastMessage = (
        <span className={styles.toastMessage}>
            <img
                className={styles.toastIcon}
                src={IMPERSONATION_ICON_SRC}
                alt=""
            />
            {t('impersonation.endingSoon', {
                time: formatCountdown(remainingSeconds),
            })}
        </span>
    );

    const closeExitModal = () => setIsExitModalOpen(false);

    const confirmExit = () => {
        closeExitModal();
        onExitSession();
    };

    const popoverContent = (
        <div className={styles.popover}>
            <div className={styles.infoSection}>
                <span className={styles.label}>
                    {t('impersonation.actingAs')}
                </span>
                <div className={styles.actingAsRow}>
                    <span className={styles.userName} title={userDisplayName}>
                        {userDisplayName}
                    </span>
                    <span
                        className={styles.timer}
                        data-testid={ImpersonationIndicatorTestIDs.Timer}
                    >
                        {formatCountdown(remainingSeconds)}
                    </span>
                </div>
            </div>
            <div className={styles.buttonSection}>
                <Button
                    isFullWidth
                    type="primary"
                    className={styles.exitButton}
                    text={t('impersonation.exitSession')}
                    onClick={() => setIsExitModalOpen(true)}
                    data-testid={ImpersonationIndicatorTestIDs.ExitButton}
                />
            </div>
        </div>
    );

    return (
        <>
            <PopoverToggle content={popoverContent} align="bottom-end">
                <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={t('impersonation.actingAs')}
                    data-testid={ImpersonationIndicatorTestIDs.IconButton}
                >
                    <img
                        className={styles.icon}
                        src={IMPERSONATION_ICON_SRC}
                        alt=""
                    />
                </button>
            </PopoverToggle>
            <DialogueAlert
                isOpen={isExitModalOpen}
                title={t('impersonation.exitDialog.title')}
                description={t('impersonation.exitDialog.description')}
                primaryButton={{
                    text: t('EXIT'),
                    onConfirm: confirmExit,
                }}
                secondaryButton={{
                    text: t('Cancel'),
                    onClick: closeExitModal,
                }}
                onDismiss={closeExitModal}
            />
            <GlobalAlert.Toast
                isVisible={isWarningToastVisible}
                message={warningToastMessage}
                onClose={() => setIsWarningToastDismissed(true)}
                autoHide={false}
                showCheckmarkIcon={false}
                primaryAction={{
                    label: t('impersonation.endSession'),
                    onClick: () => setIsExitModalOpen(true),
                }}
            />
        </>
    );
};
